import React, { createContext, useContext, useMemo, useReducer, useEffect } from "react";
import { INITIAL_REFERRALS, MEMBERS, FUNNEL, WA_TEMPLATES } from "@/data/seed";

const DemoCtx = createContext(null);
const STORAGE_KEY = "bni-md-demo-v1";

const baseInitial = {
  role: "public_registration",
  referrals: INITIAL_REFERRALS,
  registeredMember: null,
  otpVerified: false,
  members: MEMBERS,
  funnel: FUNNEL,
  waTemplates: WA_TEMPLATES,
};

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      referrals: Array.isArray(p.referrals) ? p.referrals : baseInitial.referrals,
      registeredMember: p.registeredMember || null,
      otpVerified: !!p.otpVerified,
      waTemplates: Array.isArray(p.waTemplates) ? p.waTemplates : baseInitial.waTemplates,
      role: p.role || baseInitial.role,
    };
  } catch { return null; }
}

function initState() {
  const p = loadPersisted();
  return p ? { ...baseInitial, ...p } : baseInitial;
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_ROLE":
      return { ...state, role: action.role };
    case "ADD_REFERRAL":
      return { ...state, referrals: [action.ref, ...state.referrals] };
    case "REGISTER_MEMBER":
      return { ...state, registeredMember: action.member };
    case "SET_OTP_VERIFIED":
      return { ...state, otpVerified: action.value };
    case "RETRIGGER_WA": {
      const list = state.waTemplates.map((t) =>
        t.id === action.id ? { ...t, sent: t.sent + Math.floor(Math.random() * 20) + 5 } : t
      );
      return { ...state, waTemplates: list };
    }
    case "RESET_DEMO":
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      return { ...baseInitial };
    default:
      return state;
  }
}

export function DemoProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, initState);

  // Persist only the parts that make sense to hydrate
  useEffect(() => {
    try {
      const toSave = {
        referrals: state.referrals,
        registeredMember: state.registeredMember,
        otpVerified: state.otpVerified,
        waTemplates: state.waTemplates,
        role: state.role,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch {}
  }, [state.referrals, state.registeredMember, state.otpVerified, state.waTemplates, state.role]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <DemoCtx.Provider value={value}>{children}</DemoCtx.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoCtx);
  if (!ctx) throw new Error("useDemo outside provider");
  return ctx;
}
